import deepmerge from 'deepmerge';
import {HTMLAttributes} from 'react';
import {DeepPartial} from 'redux';
import {getIsAgeRestricted} from '../../../../common/getIsAgeRestricted';
import {requestContext} from '../../../../common/requestContext';
import {twMerge} from '../../../../common/twMerge';
import {ContentHomeCardSection} from '../../../../design-system/organisms/ContentHomeCardSection/ContentHomeCardSectionMain';
import {PosterViewItem} from '../../../../design-system/organisms/PosterViewItem/PosterViewItem';
import {
  ContentHomeRecommend,
  PosterViewItem as PosterViewItemType,
} from '../../../../graphql/generated/types';
import {aggregateAndSendLog} from '../../../../logging/aggregateAndSendLog';
import {LOG_SERIES_CLICK} from '../../../../logging/logEvent';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {TrackEventObject} from '../../../../logging/TrackEventObject.interface';
import {useLogin} from '../../../../user/useLogin.hook';

export interface ContentHomeRecommendContainerProps
  extends HTMLAttributes<HTMLDivElement> {
  seriesId: number;
  recommendData: ContentHomeRecommend;
}

export function ContentHomeRecommendContainer({
  seriesId,
  recommendData,
  className,
  ...props
}: ContentHomeRecommendContainerProps) {
  const {isMobile} = requestContext.requestInfo.display;
  const {user} = useLogin();

  return (
    <div
      className={twMerge(
        'flex flex-col',
        isMobile ? 'space-y-8pxr' : 'space-y-4pxr',
        className
      )}
      {...props}
    >
      {recommendData.list.map(group => {
        if (!group.items || group.items.length === 0) {
          return null;
        }
        return (
          <ContentHomeCardSection
            key={group.id}
            {...putTiaraObjectProps({
              click: {layer1: group.impLabel},
            })}
          >
            <ContentHomeCardSection.Header>
              <ContentHomeCardSection.Header.Title
                helpDescription={group.description}
              >
                {group.title}
              </ContentHomeCardSection.Header.Title>
            </ContentHomeCardSection.Header>
            <ContentHomeCardSection.Body>
              <ContentHomeCardSection.Body.ItemList isMobile={isMobile}>
                {group.items?.map((item, index) => {
                  const posterViewItem = item as PosterViewItemType;
                  const {badgeList, ageGradeBadge, statusBadge} =
                    posterViewItem;
                  const eventLog = posterViewItem.eventLog;
                  const posterLogObj: DeepPartial<TrackEventObject> = {
                    click: {
                      ordnum: String(index + 1),
                      layer1: group.impLabel,
                    },
                    ...(eventLog?.customProps?.helix_yn === 'y' && {
                      viewimp_contents: {
                        imp_ordnum: String(index + 1),
                        layer1: group.impLabel,
                      },
                      customProps: {
                        helix_seed: String(seriesId),
                      },
                    }),
                  };
                  return (
                    <PosterViewItem
                      {...putTiaraObjectProps(
                        eventLog
                          ? deepmerge(eventLog, posterLogObj)
                          : posterLogObj
                      )}
                      key={posterViewItem.id}
                      scheme={posterViewItem.scheme || undefined}
                      onClickCapture={LOG_SERIES_CLICK}
                      className="w-98pxr cursor-pointer"
                      title={posterViewItem.title || ''}
                      altText={posterViewItem.altText || ''}
                      posterSrc={posterViewItem.thumbnail}
                      ageGradeBadge={ageGradeBadge}
                      badgeList={badgeList}
                      statusBadge={statusBadge}
                      subtitleList={posterViewItem.subtitleList}
                      isAgeRestricted={getIsAgeRestricted({
                        age: user?.age,
                        authenticatedAt: user?.authenticatedAt,
                        ageGrade: posterViewItem.ageGrade,
                        selfCensorship: posterViewItem.selfCensorship,
                      })}
                      onInView={
                        eventLog?.customProps?.helix_yn === 'y'
                          ? target => {
                              aggregateAndSendLog({
                                targetElem: target as HTMLElement,
                                actionType: 'ViewImp',
                                actionName: '작품_노출',
                                customProps: {
                                  user_action: 'imp',
                                },
                              });
                            }
                          : undefined
                      }
                      discountRateText={posterViewItem.discountRateText}
                    />
                  );
                })}
              </ContentHomeCardSection.Body.ItemList>
            </ContentHomeCardSection.Body>
          </ContentHomeCardSection>
        );
      })}
    </div>
  );
}
